// Helper functions for managing user session persistence
export const StorageKeys = {
  CLIENT_LOGGED_IN: 'unico_client_logged_in',
  CURRENT_CLIENT: 'unico_current_client',
  ADMIN_LOGGED_IN: 'unico_admin_logged_in',
  ADMIN_REMEMBER_ME: 'unico_admin_remember_me',
  REMEMBER_ME: 'unico_remember_me'
} as const

// Get the appropriate storage based on "remember me" preference
export function getStorage(rememberMe: boolean = true): Storage {
  return rememberMe ? localStorage : sessionStorage
}

// Set user session data
export function setUserSession(user: any, rememberMe: boolean = true): void {
  const storage = getStorage(rememberMe)
  try {
    storage.setItem(StorageKeys.CLIENT_LOGGED_IN, '1')
    storage.setItem(StorageKeys.CURRENT_CLIENT, JSON.stringify(user))
    // Always store remember preference in localStorage for consistency
    localStorage.setItem(StorageKeys.REMEMBER_ME, rememberMe ? '1' : '0')
  } catch (error) {
    console.warn('Failed to save user session:', error)
  }
}

// Get user session data (checks both storages)
export function getUserSession(): { user: any; rememberMe: boolean } | null {
  try {
    // Check remember me preference first
    const rememberMePref = localStorage.getItem(StorageKeys.REMEMBER_ME) === '1'
    const storage = getStorage(rememberMePref)
    
    const isLoggedIn = storage.getItem(StorageKeys.CLIENT_LOGGED_IN)
    const currentUser = storage.getItem(StorageKeys.CURRENT_CLIENT)
    
    if (isLoggedIn && currentUser) {
      return {
        user: JSON.parse(currentUser),
        rememberMe: rememberMePref
      }
    }
    
    // Fallback: check the other storage in case of mixed state
    const otherStorage = getStorage(!rememberMePref)
    const fallbackLoggedIn = otherStorage.getItem(StorageKeys.CLIENT_LOGGED_IN)
    const fallbackUser = otherStorage.getItem(StorageKeys.CURRENT_CLIENT)
    
    if (fallbackLoggedIn && fallbackUser) {
      return {
        user: JSON.parse(fallbackUser),
        rememberMe: !rememberMePref
      }
    }
    
    return null
  } catch (error) {
    console.warn('Failed to get user session:', error)
    return null
  }
}

// Clear user session data from both storages
export function clearUserSession(): void {
  try {
    // Clear from both storages to be safe
    localStorage.removeItem(StorageKeys.CLIENT_LOGGED_IN)
    localStorage.removeItem(StorageKeys.CURRENT_CLIENT)
    sessionStorage.removeItem(StorageKeys.CLIENT_LOGGED_IN)
    sessionStorage.removeItem(StorageKeys.CURRENT_CLIENT)
    localStorage.removeItem(StorageKeys.REMEMBER_ME)
  } catch (error) {
    console.warn('Failed to clear user session:', error)
  }
}

// Admin session management with remember me option
export function setAdminSession(rememberMe: boolean = false): void {
  try {
    const storage = getStorage(rememberMe)
    storage.setItem(StorageKeys.ADMIN_LOGGED_IN, '1')
    // Always store admin remember preference in localStorage for consistency
    localStorage.setItem(StorageKeys.ADMIN_REMEMBER_ME, rememberMe ? '1' : '0')
  } catch (error) {
    console.warn('Failed to save admin session:', error)
  }
}

export function getAdminSession(): { isLoggedIn: boolean; rememberMe: boolean } {
  try {
    // Check remember me preference first
    const rememberMePref = localStorage.getItem(StorageKeys.ADMIN_REMEMBER_ME) === '1'
    const storage = getStorage(rememberMePref)
    
    const isLoggedIn = storage.getItem(StorageKeys.ADMIN_LOGGED_IN) === '1'
    
    if (isLoggedIn) {
      return { isLoggedIn: true, rememberMe: rememberMePref }
    }
    
    // Fallback: check the other storage in case of mixed state
    const otherStorage = getStorage(!rememberMePref)
    const fallbackLoggedIn = otherStorage.getItem(StorageKeys.ADMIN_LOGGED_IN) === '1'
    
    if (fallbackLoggedIn) {
      return { isLoggedIn: true, rememberMe: !rememberMePref }
    }
    
    return { isLoggedIn: false, rememberMe: false }
  } catch (error) {
    console.warn('Failed to get admin session:', error)
    return { isLoggedIn: false, rememberMe: false }
  }
}

export function clearAdminSession(): void {
  try {
    // Clear from both storages to be safe
    localStorage.removeItem(StorageKeys.ADMIN_LOGGED_IN)
    sessionStorage.removeItem(StorageKeys.ADMIN_LOGGED_IN)
    localStorage.removeItem(StorageKeys.ADMIN_REMEMBER_ME)
  } catch (error) {
    console.warn('Failed to clear admin session:', error)
  }
}